import { useEffect, useState } from "react";
import {
  motion,
  AnimatePresence,
  useReducedMotion,
} from "framer-motion";
import {
  Newspaper,
  Map as MapIcon,
  Activity,
  UsersRound,
  MapPin,
  MessageSquare,
  Repeat2,
  Heart,
  ChevronRight,
  Camera,
  Video,
  Mic,
  FileText,
  Send,
  Play,
  CheckCircle2,
  Hammer,
  MessageSquareCheck,
  Target,
  PenSquare,
  ShieldCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import faceAmara from "@/assets/landing/face-amara.jpg";
import faceJonas from "@/assets/landing/face-jonas.jpg";
import faceLinh from "@/assets/landing/face-linh.jpg";
import postFlood from "@/assets/landing/post-flood.jpg";
import postStreetlight from "@/assets/landing/post-streetlight.jpg";
import postRecycling from "@/assets/landing/post-recycling.jpg";

/* -------------------------------------------------------------------------- */
/*  Screens                                                                    */
/* -------------------------------------------------------------------------- */

export type ScreenKey = "lens" | "report" | "map" | "spaces" | "pulse" | "impact";

export const PHONE_ORDER: ScreenKey[] = ["lens", "report", "map", "spaces", "pulse", "impact"];

const TABS = [
  { key: "lens", label: "Lens", icon: Newspaper },
  { key: "report", label: "Report", icon: PenSquare },
  { key: "map", label: "Map", icon: MapIcon },
  { key: "spaces", label: "Spaces", icon: UsersRound },
  { key: "pulse", label: "Pulse", icon: Activity },
] as const;

function ScreenTitle({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div className="px-4 pt-3">
      <p className="text-[8px] font-semibold uppercase tracking-[0.22em] text-white/60">{eyebrow}</p>
      <p className="mt-1 font-display text-[15px] font-bold leading-tight tracking-[-0.01em]">{title}</p>
    </div>
  );
}


function LensScreen() {
  const posts = [
    {
      name: "Amara Okoye",
      place: "Lagos",
      text: "Flooding has blocked this road since last night.",
      meta: "2m",
      face: faceAmara,
      media: postFlood,
      mediaAlt: "Floodwater covering a road beside a kerb",
      video: true,
    },
    {
      name: "Jonas Visser",
      place: "Rotterdam",
      text: "Street light out on this road — third week now.",
      meta: "18m",
      face: faceJonas,
      media: postStreetlight,
      mediaAlt: "A dark residential street with an unlit lamp",
    },
    {
      name: "Linh Tran",
      place: "Hanoi",
      text: "New community recycling point opened in our neighborhood.",
      meta: "1h",
      face: faceLinh,
      media: postRecycling,
      mediaAlt: "Sorted recycling bins on a neighbourhood street",
    },
  ];
  return (
    <div>
      <ScreenTitle eyebrow="Around you" title="What's happening" />
      <div className="mt-3 flex gap-1.5 px-4">
        {["Near me", "City", "Global"].map((c, i) => (
          <span
            key={c}
            className={cn(
              "rounded-full border px-2.5 py-1 text-[8px] font-medium",
              i === 0 ? "border-white bg-white text-black" : "border-white/15 text-white/60",
            )}
          >
            {c}
          </span>
        ))}
      </div>
      <div className="mt-2 divide-y divide-white/[0.07]">
        {posts.map((p, i) => (
          <motion.div
            key={p.name}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + i * 0.08, duration: 0.35, ease: "easeOut" }}
            className="flex gap-2.5 px-4 py-2.5"
          >
            <img
              src={p.face}
              alt=""
              width={512}
              height={512}
              loading="lazy"
              decoding="async"
              className="mt-0.5 h-6 w-6 shrink-0 rounded-full object-cover"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[9px] text-white/60">
                <span className="font-semibold text-white/85">{p.name}</span> · {p.place} · {p.meta}
              </p>
              <p className="mt-0.5 text-[10px] leading-snug text-white/80">{p.text}</p>
              {p.media && (
                <div className="relative mt-1.5 overflow-hidden rounded-lg border border-white/10">
                  <img
                    src={p.media}
                    alt={p.mediaAlt}
                    width={1024}
                    height={640}
                    loading="lazy"
                    decoding="async"
                    className="h-16 w-full object-cover"
                  />
                  {p.video && (
                    <span className="absolute bottom-1.5 left-1.5 flex items-center gap-1 rounded-full bg-black/65 px-1.5 py-[2px] text-[7px] font-semibold">
                      <Play className="h-2 w-2 fill-current" /> 0:12
                    </span>
                  )}
                </div>
              )}
              <div className="mt-1.5 flex items-center gap-4 text-white/60">
                <MessageSquare className="h-2.5 w-2.5" />
                <Repeat2 className="h-2.5 w-2.5" />
                <Heart className="h-2.5 w-2.5" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

    </div>
  );
}

function ReportScreen() {
  const reduced = useReducedMotion();
  const media = [
    { icon: Camera, label: "Photo" },
    { icon: Video, label: "Video" },
    { icon: Mic, label: "Audio" },
    { icon: FileText, label: "Doc" },
  ];
  return (
    <div>
      <ScreenTitle eyebrow="New report" title="Show what you see" />
      <div className="mx-3 mt-3 rounded-xl border border-white/12 bg-white/[0.04] p-3">
        <p className="text-[10px] leading-snug text-white/80">
          Flooding has blocked this road since last night. Water is up to the kerb outside the school gate.
        </p>
        <span className="mt-1 inline-block h-[10px] w-[1px] animate-pulse bg-white/70 align-middle" />
      </div>
      <div className="mt-2.5 grid grid-cols-4 gap-1.5 px-3">
        {media.map((m, i) => {
          const Icon = m.icon;
          return (
            <motion.span
              key={m.label}
              initial={reduced ? undefined : { opacity: 0, y: 8 }}
              animate={reduced ? undefined : { opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + i * 0.07, duration: 0.3 }}
              className={cn(
                "flex flex-col items-center gap-1 rounded-xl border py-2 text-[7.5px]",
                i === 1 ? "border-white bg-white/10 text-white" : "border-white/12 text-white/55",
              )}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={1.7} />
              {m.label}
            </motion.span>
          );
        })}
      </div>
      <div className="mx-3 mt-2.5 rounded-xl border border-white/10 bg-white/[0.03] p-2.5">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-11 shrink-0 place-items-center rounded-lg bg-white/10">
            <Play className="h-3 w-3" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[9px] font-medium text-white/80">clip-flooded-road.mp4</p>
            <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/12">
              <motion.span
                className="block h-full rounded-full bg-white"
                initial={{ width: "8%" }}
                animate={{ width: reduced ? "100%" : ["8%", "72%", "100%"] }}
                transition={reduced ? undefined : { duration: 2.2, ease: "easeOut" }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="mx-3 mt-2.5 flex items-center gap-2 rounded-xl border border-white/10 px-2.5 py-2">
        <MapPin className="h-3 w-3 shrink-0 text-white/55" />
        <p className="min-w-0 flex-1 truncate text-[9px] text-white/70">Herbert Macaulay Way · street level</p>
        <span className="text-[8px] text-white/60">Precise</span>
      </div>
      <div className="mx-3 mt-2.5 flex items-center justify-between rounded-xl bg-white px-3 py-2 text-black">
        <span className="text-[10px] font-semibold">Post report</span>
        <Send className="h-3 w-3" />
      </div>
    </div>
  );
}

function MapScreen() {
  const dots = [
    { top: "20%", left: "28%", active: false },
    { top: "36%", left: "60%", active: true },
    { top: "56%", left: "38%", active: false },
    { top: "68%", left: "70%", active: false },
  ];
  return (
    <div>
      <ScreenTitle eyebrow="Map" title="Where it is happening" />
      <div className="relative mt-3 h-[186px] overflow-hidden bg-[radial-gradient(circle_at_55%_40%,rgba(255,255,255,0.07),transparent_70%)]">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.14]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,.5) 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        />
        {dots.map((d, i) => (
          <motion.span
            key={i}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15 + i * 0.12, duration: 0.4, ease: "easeOut" }}
            className={cn(
              "absolute grid h-5 w-5 -translate-x-1/2 place-items-center rounded-full rounded-bl-none border",
              d.active ? "border-white bg-white text-black" : "border-white/25 bg-white/10 text-white",
            )}
            style={{ top: d.top, left: d.left }}
          >
            <MapPin className="h-2.5 w-2.5" />
          </motion.span>
        ))}
      </div>
      <div className="mx-3 mb-3 rounded-xl border border-white/10 bg-white/[0.05] p-2.5">
        <p className="text-[8px] font-semibold uppercase tracking-[0.16em] text-white/60">Nearby · 250 m</p>
        <p className="mt-1 text-[10px] font-semibold">Bus stop accessibility issue reported</p>
        <p className="text-[9px] text-white/60">Confirmed by neighbours · sent to the transport office</p>
      </div>
    </div>
  );
}

function SpacesScreen() {
  const thread = [
    { name: "Fatima", text: "The junction backs up every school run — has anyone timed the lights?" },
    { name: "Diego", text: "Counted 4 minutes on green southbound. Happy to record it tomorrow." },
    { name: "Aisha", text: "I'll add photos of the crossing. It's the kerb that's the problem." },
  ];
  return (
    <div>
      <ScreenTitle eyebrow="Space" title="Traffic at the junction" />
      <div className="mt-2 flex items-center gap-2 px-4 text-[8px] text-white/60">
        <UsersRound className="h-3 w-3" /> Residents discussing this junction
      </div>
      <div className="mt-2.5 space-y-2 px-3">
        {thread.map((m, i) => (
          <motion.div
            key={m.name}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + i * 0.09, duration: 0.35, ease: "easeOut" }}
            className="flex gap-2 rounded-xl border border-white/10 bg-white/[0.04] p-2.5"
          >
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-white/10 text-[8px] font-semibold">
              {m.name[0]}
            </span>
            <div className="min-w-0">
              <p className="text-[9px] font-semibold text-white/85">{m.name}</p>
              <p className="mt-0.5 text-[9.5px] leading-snug text-white/70">{m.text}</p>
            </div>
          </motion.div>
        ))}
      </div>
      <div className="mx-3 mt-2 flex items-center gap-2 rounded-xl border border-white/10 px-2.5 py-2">
        <MessageSquare className="h-3 w-3 text-white/60" />
        <p className="flex-1 text-[9px] text-white/60">Add to the discussion</p>
        <ChevronRight className="h-3 w-3 text-white/60" />
      </div>
    </div>
  );
}

function PulseScreen() {
  const bars = [34, 52, 41, 70, 58, 86, 64, 92, 76];
  const rows = [
    { label: "Road safety", note: "Most discussed" },
    { label: "Water & drainage", note: "Talked about today" },
    { label: "Public lighting", note: "Steady" },
  ];
  return (
    <div>
      <ScreenTitle eyebrow="Pulse" title="What people are talking about" />
      <div className="mt-4 flex h-24 items-end gap-1.5 px-4">
        {bars.map((b, i) => (
          <motion.span
            key={i}
            initial={{ height: 4 }}
            animate={{ height: `${b}%` }}
            transition={{ delay: i * 0.05, duration: 0.5, ease: "easeOut" }}
            className={cn("flex-1 rounded-sm", i === bars.length - 2 ? "bg-white" : "bg-white/22")}
          />
        ))}
      </div>
      <div className="mt-3 divide-y divide-white/[0.07] px-4">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between py-2">
            <p className="text-[10px] text-white/75">{r.label}</p>
            <p className="text-[9px] text-white/60">{r.note}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ImpactScreen() {
  const timeline = [
    { icon: Newspaper, label: "Reported", note: "Drainage blocked after heavy rain", done: true },
    { icon: MessageSquareCheck, label: "Response", note: "Works office accepted the report", done: true },
    { icon: Hammer, label: "In progress", note: "Crew clearing the culvert", done: true },
    { icon: CheckCircle2, label: "Completed", note: "Awaiting community verification", done: false },
  ];
  return (
    <div>
      <ScreenTitle eyebrow="Project" title="Drainage on Market Lane" />
      <div className="mx-3 mt-3 overflow-hidden rounded-xl border border-white/10">
        <div className="relative h-[74px] bg-[linear-gradient(130deg,rgba(255,255,255,0.13),rgba(255,255,255,0.02))]">
          <span className="absolute left-2 top-2 rounded-full bg-black/60 px-1.5 py-[2px] text-[7px] font-semibold uppercase tracking-[0.14em]">
            Progress update
          </span>
          <span className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded-full bg-white px-2 py-[3px] text-[8px] font-semibold text-black">
            <Play className="h-2.5 w-2.5" /> 0:24
          </span>
        </div>
      </div>
      <div className="mt-3 space-y-2 px-3">
        {timeline.map((t, i) => {
          const Icon = t.icon;
          return (
            <motion.div
              key={t.label}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + i * 0.09, duration: 0.35 }}
              className="flex items-start gap-2"
            >
              <span
                className={cn(
                  "grid h-5 w-5 shrink-0 place-items-center rounded-full border",
                  t.done ? "border-white bg-white text-black" : "border-white/25 text-white/60",
                )}
              >
                <Icon className="h-2.5 w-2.5" />
              </span>
              <div className="min-w-0 flex-1 border-b border-white/[0.06] pb-1.5">
                <p className="text-[9.5px] font-semibold text-white/85">{t.label}</p>
                <p className="truncate text-[9px] text-white/60">{t.note}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
      <div className="mx-3 mt-2 flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-2.5 py-2">
        <ShieldCheck className="h-3 w-3 shrink-0 text-white/60" />
        <p className="text-[9px] text-white/60">Residents can verify the finished work.</p>
      </div>
    </div>
  );
}

const SCREEN_BODY: Record<ScreenKey, () => React.ReactElement> = {
  lens: LensScreen,
  report: ReportScreen,
  map: MapScreen,
  spaces: SpacesScreen,
  pulse: PulseScreen,
  impact: ImpactScreen,
};

const TAB_FOR: Record<ScreenKey, string> = {
  lens: "lens",
  report: "report",
  map: "map",
  spaces: "spaces",
  pulse: "pulse",
  impact: "pulse",
};

/* -------------------------------------------------------------------------- */
/*  Device                                                                     */
/* -------------------------------------------------------------------------- */

const SCREEN_VARIANTS = {
  enter: (dir: 1 | -1) => ({ opacity: 0, x: dir * 46, scale: 0.97, filter: "blur(6px)" }),
  center: { opacity: 1, x: 0, scale: 1, filter: "blur(0px)" },
  exit: (dir: 1 | -1) => ({ opacity: 0, x: dir * -46, scale: 0.97, filter: "blur(6px)" }),
};

/**

 * The product demonstration device: a hand-built shell rendering real Xamseen
 * surfaces. Pass `screen` to drive it from the page narrative; leave it out and
 * it cycles quietly on its own. `direction` lets the caller tie the screen
 * transition to scroll direction so the device reads as one continuous flow.
 */
export function HeroPhone({
  screen,
  direction = 1,
  className,
}: {
  screen?: ScreenKey;
  direction?: 1 | -1;
  className?: string;
}) {
  const reduced = useReducedMotion();
  const [auto, setAuto] = useState<ScreenKey>("lens");
  const controlled = screen !== undefined;

  useEffect(() => {
    if (controlled || reduced) return;
    const id = window.setInterval(() => {
      setAuto((k) => PHONE_ORDER[(PHONE_ORDER.indexOf(k) + 1) % PHONE_ORDER.length]!);
    }, 3600);
    return () => window.clearInterval(id);
  }, [controlled, reduced]);

  const active = controlled ? screen! : auto;
  const Body = SCREEN_BODY[active];
  const activeTab = TAB_FOR[active];

  return (
    <div className={cn("relative mx-auto w-full max-w-[288px] sm:max-w-[300px]", className)}>
      <div className="relative rounded-[2.4rem] border border-white/12 bg-[#0a0a0a] p-2 shadow-[0_50px_130px_-50px_rgba(255,255,255,0.18)]">
        <div className="relative h-[456px] overflow-hidden rounded-[2rem] bg-[#060606] text-white">
          <div className="flex items-center justify-between px-4 pt-3 text-[9px] text-white/60">
            <span>9:41</span>
            <span className="h-1 w-8 rounded-full bg-white/12" />
            <span className="flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-white/25" />
              <span className="h-2 w-3.5 rounded-[2px] border border-white/25" />
            </span>
          </div>

          <div className="relative h-[378px]">

            <AnimatePresence mode="popLayout" initial={false} custom={direction}>
              <motion.div
                key={active}
                custom={direction}
                variants={SCREEN_VARIANTS}
                initial={reduced ? false : "enter"}
                animate={reduced ? undefined : "center"}
                exit={reduced ? undefined : "exit"}
                transition={{
                  x: { type: "spring", stiffness: 260, damping: 30, mass: 0.6 },
                  opacity: { duration: 0.32, ease: [0.22, 0.61, 0.36, 1] },
                  scale: { duration: 0.45, ease: [0.22, 0.61, 0.36, 1] },
                  filter: { duration: 0.35, ease: [0.22, 0.61, 0.36, 1] },
                }}
                className="absolute inset-0"
              >
                <Body />
              </motion.div>
            </AnimatePresence>
          </div>


          <div className="absolute inset-x-0 bottom-0 grid grid-cols-5 border-t border-white/10 bg-[#060606] px-2 py-2.5 text-[7.5px] text-white/60">
            {TABS.map((t) => {
              const Icon = t.icon;
              const on = t.key === activeTab;
              return (
                <span key={t.key} className={cn("flex flex-col items-center gap-1", on && "text-white")}>
                  <Icon className="h-3.5 w-3.5" />
                  {t.label}
                  <motion.span
                    className="h-[2px] w-4 rounded-full"
                    animate={{ opacity: on ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ background: "currentColor" }}
                  />
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export const SCREEN_ICON: Record<ScreenKey, typeof Newspaper> = {
  lens: Newspaper,
  report: PenSquare,
  map: MapIcon,
  spaces: UsersRound,
  pulse: Activity,
  impact: Target,
};
